import { useId, type ReactNode } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import {
  NOTIFICATION_TABS,
  parseNotificationTab,
  type NotificationTabId,
} from '@/lib/notification-categories';
import { cn } from '@/lib/utils';

interface Props {
  tab: NotificationTabId;
  onTabChange: (tab: NotificationTabId) => void;
  unreadOnly: boolean;
  onUnreadOnlyChange: (next: boolean) => void;
  unreadByTab: Record<NotificationTabId, number>;
  /** `compact` = inside the bell panel; `comfortable` = the full page. */
  density: 'compact' | 'comfortable';
  /** The list for the active tab. Must live inside the Tabs root, so it is
   *  passed as children rather than rendered by the caller beside us. */
  children: ReactNode;
}

function formatCount(n: number): string {
  return n > 99 ? '99+' : String(n);
}

// Tab strip + "Unread only" switch + the single panel the list renders into.
//
// One TabsContent whose value tracks the active tab, rather than seven
// mostly-empty ones: the list is the same component for every tab, only its
// data changes. Radix derives the panel's id / aria-labelledby from `value`,
// so the active tab and its panel stay correctly wired.
//
// The strip WRAPS instead of scrolling sideways. Seven tabs don't fit in a
// 344px panel, and a hidden-scrollbar strip gives mouse users no hint that
// half of them are off-screen (CODE_MAP.md records an earlier clipped-row bug
// on this exact filter row).
export function NotificationTabs({
  tab,
  onTabChange,
  unreadOnly,
  onUnreadOnlyChange,
  unreadByTab,
  density,
  children,
}: Props) {
  const unreadLabelId = useId();
  const compact = density === 'compact';

  return (
    <Tabs
      value={tab}
      onValueChange={(v) => onTabChange(parseNotificationTab(v))}
      // Compact: a flex column that fills whatever height the popover leaves,
      // so the strip stays put and only the list below it scrolls.
      className={compact ? 'flex min-h-0 flex-1 flex-col' : undefined}
    >
      <div
        className={cn(
          compact
            ? 'flex shrink-0 flex-col gap-2 border-b border-border/70 px-3 py-2'
            : 'flex flex-wrap items-center gap-x-4 gap-y-2',
        )}
      >
        <TabsList
          aria-label="Notification categories"
          className="h-auto min-w-0 flex-1 flex-wrap justify-start gap-1.5 rounded-none bg-transparent p-0"
        >
          {NOTIFICATION_TABS.map((t) => {
            const count = unreadByTab[t.id];
            return (
              <TabsTrigger
                key={t.id}
                value={t.id}
                // Same pill vocabulary as the filter chips elsewhere in the
                // app; the active state is the inverted foreground/background.
                className={cn(
                  'h-7 rounded-full border px-2.5 py-0 text-xs',
                  'border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground',
                  'data-[state=active]:border-transparent data-[state=active]:bg-foreground data-[state=active]:text-background data-[state=active]:shadow-none',
                )}
              >
                {t.label}
                {count > 0 ? (
                  <>
                    <span aria-hidden className="ml-1.5 tabular-nums opacity-80">
                      {formatCount(count)}
                    </span>
                    <span className="sr-only">, {count} unread</span>
                  </>
                ) : null}
              </TabsTrigger>
            );
          })}
        </TabsList>

        <div className={cn('flex items-center gap-2', compact ? 'justify-between' : 'ml-auto')}>
          <span id={unreadLabelId} className="text-xs font-medium text-muted-foreground">
            Unread only
          </span>
          <Switch
            checked={unreadOnly}
            onCheckedChange={onUnreadOnlyChange}
            label="Unread only"
            labelledBy={unreadLabelId}
          />
        </div>
      </div>

      {/* key: remount on tab change so the scroll position doesn't carry over
          from the previous tab. tabIndex -1 (props spread after Radix's own
          tabIndex 0): the panel's children are the focusable things, and a
          focus ring on the panel itself would be clipped by the popover. */}
      <TabsContent
        key={tab}
        value={tab}
        tabIndex={-1}
        className={cn(
          'focus-visible:ring-0',
          compact ? 'mt-0 min-h-0 flex-1 overflow-y-auto overscroll-contain' : 'mt-3',
        )}
      >
        {children}
      </TabsContent>
    </Tabs>
  );
}
